import { startMetaConversions, stopMetaConversions } from './meta-conversions';
import { loadMetaPixel, metaPixelLoaded } from './meta-pixel';
import { fuseVisitorsLoaded } from './fuse-visitors';
import { startGoogleAds, stopGoogleAds, googleAdsLoaded } from './google-ads';
import { startFreebuff, stopFreebuff, freebuffLoaded } from './freebuff';
import { landingPixelLoaded, loadLandingPixel } from './landing-pixel';
import { loadOpenAIPixel, openAIPixelLoaded } from './openai-pixel';
import { captureOpenAIAdClick } from './openai-ads';
let frame: HTMLIFrameElement | undefined;
let timer: ReturnType<typeof setInterval> | undefined;
let running = false;
let generation = 0;
let optedOut = () => true;
const production = () => import.meta.env.MODE === 'production' && typeof window !== 'undefined' && window.location.origin === 'https://userill.dev';
const gpc = () => Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl);

function dispose() {
  frame?.remove(); frame = undefined;
  if (timer) clearInterval(timer);
  timer = undefined;
}
export function stopAdvertising() {
  if (typeof window === 'undefined') return;
  generation++; running = false; dispose();
  stopMetaConversions();
  stopFreebuff();
  stopGoogleAds();
  if (production()) void fetch('/api/advertising/opt-out', {
    method: 'POST', credentials: 'same-origin', keepalive: true,
  }).catch(() => {}).finally(() => {
    // Removing a script tag cannot stop SDK timers or DOM access.
    if (metaPixelLoaded() || fuseVisitorsLoaded() || landingPixelLoaded() || openAIPixelLoaded() || freebuffLoaded() || googleAdsLoaded()) window.location.reload();
  });
  else if (freebuffLoaded() || googleAdsLoaded()) window.location.reload();
}
/** Called only after the existing analytics preference has been resolved. */
export async function startAdvertising(hasOptedOut: () => boolean, anonymousLanding = false) {
  void startMetaConversions(hasOptedOut);
  void startFreebuff(hasOptedOut);
  void startGoogleAds(hasOptedOut);
  if (!production()) return;
  optedOut = hasOptedOut;
  if (optedOut() || gpc()) { stopAdvertising(); return; }
  if (running) return;
  running = true;
  const currentGeneration = ++generation;
  try {
    const openAICapture = new URLSearchParams(window.location.search).get('reddit_pixel_test') === '1' ? undefined : captureOpenAIAdClick();
    if (openAICapture) await openAICapture;
    if (currentGeneration !== generation || optedOut() || gpc()) return;
    if (window.location.pathname === '/') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('reddit_pixel_test') !== '1' && params.get('utm_source') === 'reddit' && params.get('utm_campaign') && params.get('rdt_cid')) {
        await fetch('/api/advertising/attribution', {
          method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: 'reddit', campaign: params.get('utm_campaign'),
            content: params.get('utm_content') ?? undefined, clickId: params.get('rdt_cid') }),
        });
      }
    }
    if (anonymousLanding && window.location.pathname === '/') {
      if (currentGeneration !== generation || optedOut() || gpc()) return;
      loadMetaPixel();
      loadOpenAIPixel();
      const test = new URLSearchParams(window.location.search).get('reddit_pixel_test') === '1';
      const response = await fetch('/api/advertising/landing', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test }),
      });
      const config = await response.json() as { enabled?: boolean; pixelId?: string };
      if (currentGeneration !== generation || optedOut() || gpc()) return;
      if (config.enabled && config.pixelId) loadLandingPixel(config.pixelId);
      // A Pixel test must never create or deliver acquisition milestones.
      if (test) {
        timer = setInterval(() => { if (optedOut() || gpc()) stopAdvertising(); }, 60_000);
        return;
      }
    }
    if (currentGeneration !== generation || optedOut() || gpc()) return;
    const mount = () => {
      if (optedOut() || gpc()) { stopAdvertising(); return; }
      // Navigation destroys SDK timers. The fixed frame URL never contains private app URLs.
      frame?.remove();
      frame = document.createElement('iframe');
      frame.hidden = true; frame.title = 'Advertising measurement';
      frame.setAttribute('sandbox', 'allow-scripts');
      frame.referrerPolicy = 'no-referrer';
      frame.src = '/api/advertising/pixel';
      document.body.appendChild(frame);
    };
    mount();
    timer = setInterval(() => { mount(); if (!optedOut() && !gpc()) void startMetaConversions(optedOut); }, 60_000);
  } catch { running = false; dispose(); }
}
